import React, { useState, useEffect, memo } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import { Grid, Typography, makeStyles, useTheme, Tooltip, CircularProgress } from '@material-ui/core';
import Collapse from '@mui/material/Collapse';
import TableRow from '@mui/material/TableRow';
import { TableCell } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import { InfoOutlined } from '@material-ui/icons';
import PercentIcon from '@mui/icons-material/Percent';
import AddIcon from '@mui/icons-material/Add';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import RemoveIcon from '@mui/icons-material/Remove';
import { Draggable } from 'react-beautiful-dnd';
import { connect } from 'react-redux';
import moment from 'moment';
import { FiberManualRecord } from '@mui/icons-material';
import store from '../../../../redux/store';
import { convertDateToPST } from '../../../../helpers/audit-trail';
import CustomTableCell from './CustomTableCell';
import GroupInfoTooltip from '../GroupInfoTooltip';
import { MARKETPLACE_LABELS } from '../../../../helpers/constants';
import PricingGroupRanks from '../PricingGroupRanks';
import { setGroupsOpenned } from '../../../../redux/actions/refresh-groups-inventory';
import { setFocusGroups } from '../../../../redux/actions/groups';
import { hasMarketplaceUniquePriceAccess } from '../../../../helpers/user';
import UniquePricingModal from '../modals/UniquePricingModal';

const useStyles = makeStyles(theme => ({
  rowCellStyles: {
    padding: '1px 10px 1px 0px !important',
    textAlign: 'center !important',
  },
  firsColumnStyle: {
    padding: '1px 1px 1px 5px !important',
    minWidth: '70px !important',
  },
  ColumnGroupNameStyle: {
    padding: '1px 1px 1px 5px !important',
    minWidth: '95px !important',
  },
  typographyStyle: {
    color: theme.palette.type === 'light' ? '#3C3C3C' : '#E9E9E9',
  },
  iconStyle: {
    color: 'white',
    borderRadius: '50%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  darkBorderLine: {
    borderRight: '1px solid #5C5C5C',
    height: '30px',
  },
  normalBorderLine: {
    borderRight: '1px solid #BEBEBE',
    height: '30px',
  },
  normalDarkRowColor1: {
    background: '#262C2E',
  },
  normalDarkRowColor2: {
    background: '#2D3436',
  },
  normalLightColor: {
    background: '#FFFFFF',
  },
  badGroupMainRow: {
    borderTop: `2px solid ${theme.palette.type === 'light' ? '#B71C1C' : '#AA6161'}`,
    borderBottom: `0px`,
    borderRight: `2px solid ${theme.palette.type === 'light' ? '#B71C1C' : '#AA6161'}`,
    borderLeft: `2px solid ${theme.palette.type === 'light' ? '#B71C1C' : '#AA6161'}`,
    transition: theme.transitions.easing.easeInOut,
    borderRadius: '2px',
    backgroundColor: theme.palette.type === 'light' ? '#F0D3D3' : '#AA6161',
  },
  badGroupCollapsableRow: {
    borderTop: `0px `,
    borderBottom: `2px solid ${theme.palette.type === 'light' ? '#B71C1C' : '#AA6161'}`,
    borderRight: `2px solid ${theme.palette.type === 'light' ? '#B71C1C' : '#AA6161'}`,
    borderLeft: `2px solid ${theme.palette.type === 'light' ? '#B71C1C' : '#AA6161'}`,
    transition: theme.transitions.easing.easeInOut,
    borderRadius: '2px',
  },
  focusedGroupMainRow: {
    borderTop: `2px solid ${theme.palette.primary.main}`,
    borderBottom: `0px`,
    borderRight: `2px solid ${theme.palette.primary.main}`,
    borderLeft: `2px solid ${theme.palette.primary.main}`,
    transition: theme.transitions.easing.easeInOut,
    borderRadius: '2px',
    backgroundColor: theme.palette.type === 'light' ? '#F4E5F7' : '#85508E',
  },
  focusedGroupCollapsableRow: {
    borderTop: `0px `,
    borderBottom: `2px solid ${theme.palette.primary.main}`,
    borderRight: `2px solid ${theme.palette.primary.main}`,
    borderLeft: `2px solid ${theme.palette.primary.main}`,
    transition: theme.transitions.easing.easeInOut,
    borderRadius: '2px',
  },
  draggingFocusedGroupMainRow: {
    borderTop: `0px `,
    borderBottom: `0px`,
    borderRight: `0px `,
    borderLeft: `0px `,
    backgroundColor: theme.palette.type === 'light' ? '#F4E5F7' : '#85508E',
  },
  draggingBadGroupMainRow: {
    borderTop: `0px `,
    borderBottom: `0px`,
    borderRight: `0px `,
    borderLeft: `0px `,
    backgroundColor: theme.palette.type === 'light' ? '#F0D3D3' : '#AA6161',
  },
  draggingGroupCollapsableRow: {
    borderTop: `0px `,
    borderBottom: `0px `,
    borderRight: `0px `,
    borderLeft: `0px `,
  },
  addRemoveIconStyle: {
    color: theme.palette.type === 'light' ? '#45505E' : '#E9E9E9',
  },
  typographyExpandableStyle1: {
    fontSize: '12px',
    lineHeight: '15.96px',
    fontWeight: 400,
    color: theme.palette.type === 'light' ? '#262626' : '#E9E9E9',
  },
  typographyExpandableStyle2: {
    fontSize: '14px',
    lineHeight: '18.62px',
    fontWeight: 400,
    color: theme.palette.type === 'light' ? '#262626' : '#E9E9E9',
  },
  loadingContent: {
    padding: '15px',
  },
  uniquePricingIcon: {
    fontSize: 8,
    color: theme.palette.type === 'light' ? '#9C27B0' : '#BA68C8',
  },
}));

const getLastSoldDate = group => {
  const lastSale = 'Last Sale: ';
  const lastSoldDate = group?.last_sold_date;
  if (lastSoldDate) {
    const pstDate = convertDateToPST(lastSoldDate);
    const date = moment(pstDate).format('M/D/YYYY Th:mm A');
    return `${lastSale}${date}`;
  }

  return `${lastSale}`;
};

const getEffectivePriceChange = group => {
  const lastDateChange = group?.last_date_change;
  const lastApiChange = group?.last_api_change;
  const dateChangeMs = lastDateChange ? moment(lastDateChange).valueOf() : 0;
  const apiChangeMs = lastApiChange ? moment(lastApiChange).valueOf() : 0;
  if (apiChangeMs > dateChangeMs) {
    return { date: lastApiChange, source: 'API' };
  }
  return { date: lastDateChange, source: group?.last_user_change };
};

const getLastDateChange = group => {
  const lastDateChangeText = 'Last Price Change: ';
  const { date } = getEffectivePriceChange(group);
  if (date) {
    const pstDate = convertDateToPST(date);
    return `${lastDateChangeText}${moment(pstDate).format('M/D/YYYY Th:mm A')}`;
  }
  return lastDateChangeText;
};

const getLastUserChange = group => {
  const lastUserChangeText = 'By user: ';
  const { source } = getEffectivePriceChange(group);
  return source ? `${lastUserChangeText}${source}` : lastUserChangeText;
};

const GroupRow = props => {
  const {
    filteredStubhubListings,
    focusedPricingGroup,
    groupedListings,
    index,
    isAbTest,
    isCompAllGroup,
    isDragging,
    rowInfo,
    selectedIndex,
    setSelectedIndex,
    setUpdateListingsOnSave,
    settings,
    typeListing,
    badGroups,
    allGroupsLength,
    groupsOpenned,
    marketplaces,
    groupsBasicInfo,
    handleGroupAttrEdit,
  } = props;

  const [open, setOpen] = useState(false);
  const [mainRowClasess, setMainRowClasses] = useState('');
  const [collapsableRowClasess, setCollapsableRowClasses] = useState('');
  const [spaceout, setSpaceout] = useState('');
  const [increment, setIncrement] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);

  const classes = useStyles();
  const theme = useTheme();

  const skyboxIds = rowInfo?.ranks?.map(rank => rank.skybox_listing_id) || [];
  const broadcasted = groupedListings.filter(gl => skyboxIds.includes(gl.skybox_id) && Boolean(gl.broadcast));
  const floor = rowInfo.floor ? parseFloat(rowInfo.floor.replace('$', '')) : 0;
  const ceiling = rowInfo.ceiling ? parseFloat(rowInfo.ceiling.replace('$', '')) : 0;

  const hashBroadcast = broadcasted.reduce((acum = {}, curr) => {
    // eslint-disable-next-line no-param-reassign
    acum[curr.skybox_id] = true;
    return acum;
  }, {});

  const group = rowInfo;
  const isFocused = focusedPricingGroup === rowInfo.id;

  const averageValueGroup = () => {
    let value = 0;
    let groupLength = 0;

    group.ranks.forEach(rank => {
      const groups = groupedListings.filter(gl => gl.skybox_id === rank.skybox_listing_id);
      groups.forEach(g => {
        value += g.cost.total;
        groupLength += g.quantity;
      });
    });
    return Math.round(value / groupLength);
  };

  const getValuation = () => {
    const valuation = averageValueGroup();
    return `$${valuation || 0}`;
  };

  const handleOnClickButton = event => {
    event.persist();
    event.stopPropagation();
    const newGroupsOppened = [...store.getState().refreshGroupsInventory.groupsOpenned];
    if (!open) {
      if (!isFocused) {
        store.dispatch(setFocusGroups({ pricingGroup: group, groupId: null, event, index, view: 'max' }));
      }
      if (!newGroupsOppened.includes(rowInfo.id)) {
        newGroupsOppened.push(rowInfo.id);
        store.dispatch(setGroupsOpenned(newGroupsOppened));
      }
    } else if (isFocused) {
      store.dispatch(setFocusGroups({ pricingGroup: group, groupId: null, event, index, view: 'max' }));
      newGroupsOppened.pop();
      store.dispatch(setGroupsOpenned(newGroupsOppened));
      if (newGroupsOppened.length > 0) {
        const lastFocusedGroupId = newGroupsOppened[newGroupsOppened.length - 1];
        const newFocusGroupIndex = store
          .getState()
          .refreshGroups.groups.findIndex(item => item.id === lastFocusedGroupId);
        if (newFocusGroupIndex !== -1) {
          store.dispatch(
            setFocusGroups({
              pricingGroup: store.getState().refreshGroups.groups[newFocusGroupIndex],
              groupId: null,
              event: null,
              index: newFocusGroupIndex,
              view: 'max',
            })
          );
        }
      }
    } else if (open) {
      const indexToRemove = newGroupsOppened.findIndex(item => item === rowInfo.id);
      if (indexToRemove !== -1) {
        newGroupsOppened.splice(indexToRemove, 1);
        store.dispatch(setGroupsOpenned(newGroupsOppened));
      }
    }
    setOpen(!open);
  };

  const handleOnClickRow = event => {
    event.persist();
    if (!isModalOpen) {
      if (open && !isFocused) {
        const newGroupsOppened = [...store.getState().refreshGroupsInventory.groupsOpenned];
        const indexToRemove = newGroupsOppened.findIndex(item => item === rowInfo.id);
        if (indexToRemove !== -1) {
          newGroupsOppened.splice(indexToRemove, 1);
          newGroupsOppened.push(rowInfo.id);
          store.dispatch(setGroupsOpenned(newGroupsOppened));
        }
      }
      store.dispatch(setFocusGroups({ pricingGroup: group, groupId: null, event, index, view: 'max' }));
    }
  };

  const getGroupClass = isError => {
    if (isError) {
      if (isDragging !== 0) {
        return [classes.draggingBadGroupMainRow, classes.draggingGroupCollapsableRow];
      }
      return [classes.badGroupMainRow, classes.badGroupCollapsableRow];
    }
    if (group?.id === focusedPricingGroup) {
      if (isDragging !== 0) {
        return [classes.draggingFocusedGroupMainRow, classes.draggingGroupCollapsableRow];
      }
      return [classes.focusedGroupMainRow, classes.focusedGroupCollapsableRow];
    }
    return ['', ''];
  };

  useEffect(() => {
    const isGroupError = badGroups && badGroups.some(badGroup => badGroup.id === group?.id);
    const UpdatedClass = getGroupClass(isGroupError);
    setMainRowClasses(UpdatedClass[0]);
    setCollapsableRowClasses(UpdatedClass[1]);
  }, [focusedPricingGroup, badGroups, group, theme, isDragging]);

  useEffect(() => {
    if (group?.id === focusedPricingGroup && selectedIndex === -1) {
      setSelectedIndex(index);
    }
  }, [focusedPricingGroup]);

  useEffect(() => {
    if (rowInfo.spaceout_type === '%') {
      setSpaceout(`${rowInfo.spaceout}${rowInfo.spaceout_type}`);
    } else {
      setSpaceout(`${rowInfo.spaceout_type} ${rowInfo.spaceout}`);
    }
    if (rowInfo.increment_type === '%') {
      setIncrement(`${rowInfo.increment}${rowInfo.increment_type}`);
    } else {
      setIncrement(`${rowInfo.increment_type} ${rowInfo.increment}`);
    }
  }, [rowInfo.spaceout_type, rowInfo.increment_type, rowInfo.increment, rowInfo.spaceout]);

  useEffect(() => {
    const isOpen = groupsOpenned.includes(rowInfo.id);
    if (isOpen !== open) {
      if (open && isFocused) {
        store.dispatch(setFocusGroups({ pricingGroup: group, groupId: null, event: null, index, view: 'max' }));
      }
      setOpen(isOpen);
    }
  }, [groupsOpenned]);

  const groupInfoHover = groupInfo => (
    <GroupInfoTooltip
      lastPriceChange={getLastDateChange(groupInfo)}
      lastPriceChangeUser={getLastUserChange(groupInfo)}
      lastSoldDate={getLastSoldDate(groupInfo)}
    >
      <InfoOutlined style={{ color: theme.palette.type === 'dark' ? '#A049AF' : '#9C27B0', fontSize: 25 }} />
    </GroupInfoTooltip>
  );

  // Check if unique pricing data exists for any marketplace
  const hasTMRUniquePricing = rowInfo?.tmr_unique_pricing?.markupdown_value > 0;
  const hasSHUniquePricing = rowInfo?.sh_unique_pricing?.markupdown_value > 0;
  const hasVSUniquePricing = rowInfo?.vs_unique_pricing?.markupdown_value > 0;
  const hasAnyUniquePricing = hasTMRUniquePricing || hasSHUniquePricing || hasVSUniquePricing;
  const hasAnyUniquePricingEnabled =
    (rowInfo?.tmr_unique_pricing?.status === 'on' && rowInfo?.tmr_unique_pricing?.markupdown_value > 0) ||
    (rowInfo?.sh_unique_pricing?.status === 'on' && rowInfo?.sh_unique_pricing?.markupdown_value > 0) ||
    (rowInfo?.vs_unique_pricing?.status === 'on' && rowInfo?.vs_unique_pricing?.markupdown_value > 0);

  return (
    <React.Fragment>
      <Draggable key={rowInfo.id} draggableId={`${rowInfo.id}`} index={index} isDragDisabled={allGroupsLength === 1}>
        {providedLi => (
          <>
            <TableRow
              ref={providedLi.innerRef}
              {...providedLi.draggableProps}
              className={`${mainRowClasess} ${
                // eslint-disable-next-line no-nested-ternary
                theme.palette.type === 'dark'
                  ? index % 2 === 0
                    ? classes.normalDarkRowColor1
                    : classes.normalDarkRowColor2
                  : classes.normalLightColor
              }`}
              onClick={e => handleOnClickRow(e)}
            >
              <CustomTableCell className={classes.firsColumnStyle}>
                <Grid container direction="row" wrap="nowrap" alignItems="center">
                  <Grid item {...providedLi.dragHandleProps} style={{ paddingTop: '5px' }}>
                    <DragIndicatorIcon style={{ rotate: '90deg', color: '#BEBEBE' }} />
                  </Grid>
                  <Grid item>
                    <IconButton aria-label="expand row" size="small" onClick={e => handleOnClickButton(e)}>
                      {open ? (
                        <RemoveIcon className={classes.addRemoveIconStyle} />
                      ) : (
                        <AddIcon className={classes.addRemoveIconStyle} />
                      )}
                    </IconButton>
                  </Grid>
                  {hasMarketplaceUniquePriceAccess() && hasAnyUniquePricingEnabled && (
                    <Grid item style={{ paddingTop: '5px', marginTop: '-2px' }}>
                      <FiberManualRecord className={classes.uniquePricingIcon} fontSize="8" />
                    </Grid>
                  )}
                </Grid>
              </CustomTableCell>
              <CustomTableCell
                className={classes.ColumnGroupNameStyle}
                typographyClassName={classes.typographyStyle}
                align="right"
                hasTooltip
                tooltip="Group Name"
              >
                {rowInfo.name}
              </CustomTableCell>
              <CustomTableCell
                className={classes.rowCellStyles}
                typographyClassName={classes.typographyStyle}
                align="right"
                tooltip="Floor"
                hasTooltip
              >
                {rowInfo.floor}
              </CustomTableCell>
              <CustomTableCell
                className={classes.rowCellStyles}
                typographyClassName={classes.typographyStyle}
                align="right"
                tooltip="Ceiling"
                hasTooltip
              >
                {rowInfo.ceiling}
              </CustomTableCell>
              <CustomTableCell
                className={classes.rowCellStyles}
                typographyClassName={classes.typographyStyle}
                align="right"
                tooltip="Increment"
                hasTooltip
              >
                {increment}
              </CustomTableCell>
              <CustomTableCell
                className={classes.rowCellStyles}
                typographyClassName={classes.typographyStyle}
                align="right"
                tooltip="Spaceout"
                hasTooltip
              >
                {rowInfo.dynamic_spaceout_enabled ? (
                  <Typography color="textSecondary" style={{ backgroundColor: '#9C27B030', borderRadius: '6px' }}>
                    Dynamic
                  </Typography>
                ) : (
                  <Typography>{spaceout}</Typography>
                )}
              </CustomTableCell>
              <CustomTableCell
                className={classes.rowCellStyles}
                typographyClassName={classes.typographyStyle}
                align="right"
                hasTooltip
                tooltip="Anchor"
              >
                {rowInfo.anchor}
              </CustomTableCell>
              <CustomTableCell
                className={classes.rowCellStyles}
                typographyClassName={classes.typographyStyle}
                align="right"
                hasTooltip
                tooltip="Tickets"
              >
                {rowInfo.quantity}
              </CustomTableCell>
              <CustomTableCell
                className={classes.rowCellStyles}
                typographyClassName={classes.typographyStyle}
                align="right"
                hasTooltip
                tooltip="Listings"
              >
                {rowInfo.listing}
              </CustomTableCell>
            </TableRow>

            {open ? (
              <TableRow
                {...providedLi.draggableProps}
                className={collapsableRowClasess}
                onClick={e => handleOnClickRow(e)}
              >
                <TableCell style={{ paddingBottom: 0, paddingTop: 0, paddingLeft: 0, paddingRight: 0 }} colSpan={9}>
                  <Collapse
                    in={open && isDragging !== rowInfo.id}
                    timeout={isDragging === 0 ? 'auto' : '0'}
                    unmountOnExit
                  >
                    <Box style={{ margin: '0px' }}>
                      <Grid
                        container
                        spacing={1}
                        direction="row"
                        justifyContent="space-between"
                        style={{
                          padding: '5px 10px 5px 10px',
                          margin: '0px',
                          width: '100%',
                          background: theme.palette.type === 'light' ? '#FFFFFF' : '#2A3032',
                        }}
                      >
                        <Grid
                          item
                          onClick={e => {
                            e.stopPropagation();
                          }}
                        >
                          <Grid container spacing={1}>
                            <Grid item>
                              <Grid container justifyContent="flex-start">
                                <Typography className={classes.typographyExpandableStyle1}>Marketplace:</Typography>
                              </Grid>
                              <Grid container justifyContent="flex-start" style={{ marginTop: '5px' }}>
                                <Typography
                                  style={{
                                    fontSize: '14px',
                                    lineHeight: '18.62px',
                                    fontWeight: 400,
                                    color: theme.palette.type === 'light' ? '#9C27B0' : '#A049AF',
                                  }}
                                >
                                  {rowInfo.marketplace && rowInfo.marketplace.length > 0
                                    ? MARKETPLACE_LABELS[rowInfo.marketplace[0]]
                                    : ''}
                                </Typography>
                              </Grid>
                            </Grid>
                            {!isAbTest && group ? (
                              <Grid item style={{ marginLeft: '25px' }}>
                                <Grid container justifyContent="flex-start">
                                  <Typography className={classes.typographyExpandableStyle1}>Average Cost</Typography>
                                </Grid>
                                <Grid container justifyContent="flex-start" style={{ marginTop: '4px' }}>
                                  <Typography className={classes.typographyExpandableStyle2}>
                                    {getValuation()}
                                  </Typography>
                                </Grid>
                              </Grid>
                            ) : (
                              <></>
                            )}
                            <Grid item style={{ marginLeft: '25px' }}>
                              <Grid container justifyContent="flex-start">
                                <Typography className={classes.typographyExpandableStyle1}>Pricing Strategy</Typography>
                              </Grid>
                              <Grid container justifyContent="flex-start" style={{ marginTop: '4px' }}>
                                <Typography className={classes.typographyExpandableStyle2}>
                                  {rowInfo.PricingStrategy}
                                </Typography>
                              </Grid>
                            </Grid>
                          </Grid>
                        </Grid>

                        <Grid item>
                          <Grid container>
                            {hasMarketplaceUniquePriceAccess() && hasAnyUniquePricing && (
                              <UniquePricingModal
                                rowInfo={rowInfo}
                                handleGroupAttrEdit={handleGroupAttrEdit}
                                disabled={!isFocused}
                              />
                            )}
                            {group && (group?.discount || group?.sg_discount) ? (
                              <Grid
                                item
                                style={{ paddingTop: '6px', marginTop: '1px' }}
                                className={
                                  theme.palette.type === 'dark' ? classes.darkBorderLine : classes.normalBorderLine
                                }
                              >
                                <Grid
                                  container
                                  justifyContent="center"
                                  alignContent="center"
                                  style={{ paddingRight: '10px' }}
                                >
                                  <Tooltip
                                    title={[
                                      group?.discount && `GameTime: ${group.discount}%`,
                                      group?.sg_discount && `SeatGeek: ${group.sg_discount}%`,
                                    ]
                                      .filter(Boolean)
                                      .join(' | ')}
                                  >
                                    <div
                                      className={classes.iconStyle}
                                      style={
                                        theme.palette.type === 'dark'
                                          ? { backgroundColor: '#A049AF' }
                                          : { backgroundColor: '#9C27B0' }
                                      }
                                    >
                                      <PercentIcon style={{ fontSize: '20px' }} />
                                    </div>
                                  </Tooltip>
                                </Grid>
                              </Grid>
                            ) : (
                              <></>
                            )}
                            <Grid item style={{ paddingTop: '5px', paddingLeft: '9px' }}>
                              {groupInfoHover(rowInfo)}
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                      {groupsBasicInfo ? (
                        <div
                          className={classes.loadingContent}
                          style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                          }}
                        >
                          <CircularProgress />
                        </div>
                      ) : (
                        <div>
                          {rowInfo.ranks.length > 0 && groupedListings.length > 0 && (
                            <PricingGroupRanks
                              ceiling={ceiling}
                              filteredStubhubListings={filteredStubhubListings}
                              floor={floor}
                              groupedListings={groupedListings}
                              groupName={rowInfo.name}
                              hashBroadcast={hashBroadcast}
                              increment={rowInfo.increment}
                              incrementType={rowInfo.increment_type}
                              index={index}
                              isAbTest={isAbTest}
                              isCompAllGroup={isCompAllGroup}
                              isFocused={isFocused}
                              isModalOpen={isModalOpen}
                              focusedPricingGroup={focusedPricingGroup}
                              manualPrice={rowInfo.manual_price}
                              pricingGroup={rowInfo}
                              ranks={rowInfo.ranks}
                              setIsModalOpen={setIsModalOpen}
                              settings={settings}
                              spaceout={rowInfo.spaceout}
                              spaceoutType={rowInfo.spaceout_type}
                              hasDynamicSpaceout={Boolean(rowInfo.has_dynamic_spaceout)}
                              dynamicSpaceoutEnabled={Boolean(rowInfo.dynamic_spaceout_enabled)}
                              groupId={rowInfo.id}
                              splits={rowInfo.splits}
                              group={group}
                              broadcast={Boolean(rowInfo.auto_broadcast)}
                              setUpdateListingsOnSave={setUpdateListingsOnSave}
                              typeListing={typeListing}
                              tmrMarkUpDown={rowInfo?.tmr_unique_pricing?.markupdown_value ?? 0}
                              shMarkUpDown={rowInfo?.sh_unique_pricing?.markupdown_value ?? 0}
                              vsMarkUpDown={rowInfo?.vs_unique_pricing?.markupdown_value ?? 0}
                              marketplaces={marketplaces}
                            />
                          )}
                        </div>
                      )}
                    </Box>
                  </Collapse>
                </TableCell>
              </TableRow>
            ) : (
              <TableRow className={collapsableRowClasess} />
            )}
          </>
        )}
      </Draggable>
    </React.Fragment>
  );
};

GroupRow.defaultProps = {
  marketplaces: null,
};

GroupRow.propTypes = {
  filteredStubhubListings: PropTypes.array.isRequired,
  focusedPricingGroup: PropTypes.number.isRequired,
  groupedListings: PropTypes.array.isRequired,
  index: PropTypes.number.isRequired,
  isAbTest: PropTypes.bool.isRequired,
  isCompAllGroup: PropTypes.bool.isRequired,
  isDragging: PropTypes.bool.isRequired,
  rowInfo: PropTypes.object.isRequired,
  selectedIndex: PropTypes.object.isRequired,
  setSelectedIndex: PropTypes.func.isRequired,
  setUpdateListingsOnSave: PropTypes.func.isRequired,
  settings: PropTypes.object.isRequired,
  typeListing: PropTypes.string.isRequired,
  groupsOpenned: PropTypes.array.isRequired,
  badGroups: PropTypes.array.isRequired,
  allGroupsLength: PropTypes.number.isRequired,
  marketplaces: PropTypes.object,
  groupsBasicInfo: PropTypes.bool.isRequired,
  handleGroupAttrEdit: PropTypes.func.isRequired,
};

function mapStateToProps(state) {
  return {
    badGroups: state.groups.badGroups,
    groupsBasicInfo: state.refreshGroups.groupsBasicInfo,
  };
}

const areEqual = (prevProps, nextProps) => {
  const propChanged = Object.keys(prevProps).some(key => {
    if (prevProps[key] !== nextProps[key]) {
      if (key === 'rowInfo' || key === 'settings' || key === 'marketplaces') {
        if (JSON.stringify(prevProps[key]) !== JSON.stringify(nextProps[key])) {
          return true;
        }
        return false;
      }
      return true;
    }
    return false;
  });
  return !propChanged;
};

export default memo(connect(mapStateToProps)(GroupRow), areEqual);
